// Endless sliding row. Each [data-marquee-track] holds its items twice, so moving it by half its
// width loops seamlessly. Leftward in LTR, rightward in RTL, at data-marquee-speed px/second.
// Prefers Web Animations (compositor-smooth); falls back to requestAnimationFrame where the host
// pauses animations (e.g. an editor preview).
class MarqueeElement extends HTMLElement {
  connectedCallback() {
    this.tracks = Array.from(this.querySelectorAll('[data-marquee-track]'));
    this.animations = [];
    this.direction = document.documentElement.dir === 'rtl' ? 1 : -1;
    // Images may still be loading; size the loop once layout is ready.
    requestAnimationFrame(() => this.start());
  }

  disconnectedCallback() {
    clearTimeout(this.checkTimer);
    cancelAnimationFrame(this.frame);
    for (const animation of this.animations) animation.cancel();
  }

  start() {
    this.loops = this.tracks
      .map((track) => ({
        track,
        distance: track.scrollWidth / 2,
        speed: Number(track.dataset.marqueeSpeed) || 50,
      }))
      .filter((loop) => loop.distance > 0);

    if (typeof this.animate !== 'function') {
      this.startFrameLoop();
      return;
    }
    for (const loop of this.loops) {
      this.animations.push(
        loop.track.animate(
          [{ transform: 'translate3d(0, 0, 0)' }, { transform: `translate3d(${this.direction * loop.distance}px, 0, 0)` }],
          { duration: (loop.distance / loop.speed) * 1000, iterations: Infinity, easing: 'linear' },
        ),
      );
    }
    this.checkTimer = setTimeout(() => {
      const first = this.animations[0];
      const advanced = first && Number(first.currentTime) > 100 && first.playState === 'running';
      if (first && !advanced) this.startFrameLoop();
    }, 600);
  }

  startFrameLoop() {
    for (const animation of this.animations) animation.cancel();
    this.animations = [];
    this.startTime = performance.now();
    this.frame = requestAnimationFrame(this.tick);
  }

  tick = (now) => {
    const seconds = (now - this.startTime) / 1000;
    for (const loop of this.loops) {
      const offset = (seconds * loop.speed) % loop.distance;
      loop.track.style.transform = `translate3d(${this.direction * offset}px, 0, 0)`;
    }
    this.frame = requestAnimationFrame(this.tick);
  };
}

if (!customElements.get('s-marquee')) {
  customElements.define('s-marquee', MarqueeElement);
}
